import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings } from 'lucide-react';

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [functionalCookies, setFunctionalCookies] = useState(true);
  const [analyticsCookies, setAnalyticsCookies] = useState(false);
  const [marketingCookies, setMarketingCookies] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Small delay before showing banner
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleFunctionalOnly = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      functional: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      functional: functionalCookies,
      analytics: analyticsCookies,
      marketing: marketingCookies,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" />
      
      {/* Cookie Banner - Centered */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">
          {/* Header */}
          <div className="bg-[#1e3a5f] px-6 py-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-white text-lg font-semibold">Cookie-Einstellungen</h2>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {!showSettings ? (
              <>
                <p className="text-slate-700 text-sm leading-relaxed mb-4">
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                  Einige Cookies sind für den Betrieb der Website notwendig, während andere uns helfen, 
                  die Website zu verbessern und Ihnen personalisierte Inhalte anzuzeigen.
                </p>
                
                <p className="text-slate-600 text-sm mb-5">
                  Weitere Informationen finden Sie in unserer{' '}
                  <Link 
                    to="/datenschutz" 
                    className="text-red-600 hover:underline font-medium"
                  >
                    Datenschutzerklärung
                  </Link>
                  {' '}und unseren{' '}
                  <Link 
                    to="/cookie-richtlinie" 
                    className="text-red-600 hover:underline font-medium"
                  >
                    Cookie-Richtlinien
                  </Link>.
                </p>

                {/* Settings Link */}
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm mb-5 transition-colors"
                  data-testid="cookie-settings-link"
                >
                  <Settings className="w-4 h-4" />
                  <span>Cookie-Einstellungen anpassen</span>
                </button>

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleFunctionalOnly}
                    className="flex-1 bg-white border-2 border-slate-300 hover:border-slate-400 text-slate-700 font-medium py-3 px-4 rounded-lg transition-colors"
                    data-testid="cookie-functional-only"
                  >
                    Nur Notwendige
                  </button>
                  
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid="cookie-accept-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Alle akzeptieren
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Settings View */}
                <p className="text-slate-600 text-sm mb-5">
                  Hier können Sie Ihre Cookie-Einstellungen individuell anpassen.
                </p>
                
                <div className="space-y-4 mb-6">
                  {/* Functional Cookies - Always required */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-700">Notwendige Cookies</p>
                      <p className="text-xs text-slate-500">Erforderlich für die Grundfunktionen der Website</p>
                    </div>
                    <input 
                      type="checkbox" 
                      checked={functionalCookies} 
                      disabled 
                      className="w-5 h-5 accent-red-600 cursor-not-allowed"
                    />
                  </div>
                  
                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-700">Analyse-Cookies</p>
                      <p className="text-xs text-slate-500">Helfen uns, die Website zu verbessern</p>
                    </div>
                    <input 
                      type="checkbox" 
                      checked={analyticsCookies}
                      onChange={(e) => setAnalyticsCookies(e.target.checked)}
                      className="w-5 h-5 accent-red-600 cursor-pointer"
                    />
                  </div>
                  
                  {/* Marketing Cookies */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-700">Marketing-Cookies</p>
                      <p className="text-xs text-slate-500">Für personalisierte Inhalte und Werbung</p>
                    </div>
                    <input 
                      type="checkbox" 
                      checked={marketingCookies}
                      onChange={(e) => setMarketingCookies(e.target.checked)}
                      className="w-5 h-5 accent-red-600 cursor-pointer"
                    />
                  </div>
                </div>
                
                {/* Settings Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowSettings(false)}
                    className="flex-1 bg-white border-2 border-slate-300 hover:border-slate-400 text-slate-700 font-medium py-3 px-4 rounded-lg transition-colors"
                  >
                    Zurück
                  </button>
                  <button
                    onClick={handleSaveSettings}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                    data-testid="cookie-save-settings"
                  >
                    Einstellungen speichern
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
